import React from 'react';
import { Link, useLocation } from 'react-router-dom';

import { Container, Row, Form, FormGroup, Input, Button } from 'reactstrap';

import bannerLogo from '../images/Jook_Web_White.png';

/** Presentation */
import ErrorMessage from '../components/ErrorMessage';

/** Custom Hooks */
import useErrorHandler from '../utils/custom-hooks/ErrorHandler';

/** Context */
import { authContext } from '../contexts/AuthContext';

/** Utils */
import { validateLoginForm } from '../utils/Helpers';

import * as JKYModel from '../custom-types';

import api from '../utils/Api';
import ResendVerification from './ResendVerification';
import GoogleSignInButton from './GoogleSignInButton';
import GoogleUsernameStep from './GoogleUsernameStep';
import { GoogleSignInResult, PendingGoogleSignup } from '../utils/GoogleSignIn';
import TwoStepCodeStep from './TwoStepCodeStep';
import { MFA_REQUIRED } from '../utils/TwoStep';

import './LoginView.scss';
import { trackEvent } from '../utils/Analytics';
import FooterContent from './FooterContent';
import AboutJookable from './AboutJookable';

const LoginView = () => {
  const location = useLocation();
  const [userEmail, setUserEmail] = React.useState('');
  const [userPassword, setUserPassword] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const context = React.useContext(authContext);
  const { error, showError } = useErrorHandler(null);
  // The account's email when the password was right but the email isn't confirmed yet.
  const [unverifiedEmail, setUnverifiedEmail] = React.useState('');
  // Someone new who picked their Google account, and now picks a username.
  const [googlePending, setGooglePending] = React.useState<PendingGoogleSignup | null>(null);
  // The password (or Google) was right and the account has two-step sign-in on: the code
  // from their app comes next. Remembers how they started, for the sign-in event.
  const [secondStep, setSecondStep] = React.useState<{ mfaToken: string; method: 'email' | 'google' } | null>(null);

  // Signing in sets the auth status; Public then sends them on to where they were headed.
  const signedIn = (user: JKYModel.User, token: string, event: 'login' | 'sign_up', method = 'google') => {
    trackEvent(event, { method });
    context.setAuthStatus({ id: user.id, email: user.email, token: token });
    context.updateCurrentUser(user);
  };

  const handleGoogleResult = (result: GoogleSignInResult) => {
    if (result.kind === 'pending') {
      showError(null);
      setGooglePending(result.pending);
    } else if (result.kind === 'mfa') {
      showError(null);
      setSecondStep({ mfaToken: result.mfaToken, method: 'google' });
    } else {
      signedIn(result.user, result.token, 'login');
    }
  };

  const authHandler = async () => {
    setLoading(true);
    setUnverifiedEmail('');
    api
      .auth('login', 'POST', {
        email: userEmail,
        password: userPassword,
      })
      .then(response => {
        console.log(response);
        let user: JKYModel.User;
        let token: string;
        if (response.user) {
          user = response.user;
        }
        if (response.token) {
          token = response.token;
        }
        if (user && token) {
          trackEvent('login', { method: 'email' });
          context.setAuthStatus({ id: user.id, email: user.email, token: token });
          context.updateCurrentUser(user);
        }

        setLoading(false);
      })
      .catch(error => {
        setLoading(false);
        if (error.code === MFA_REQUIRED && error.mfaToken) {
          showError(null);
          setUserPassword('');
          setSecondStep({ mfaToken: error.mfaToken, method: 'email' });
          return;
        }
        showError(error.message);
        if (error.code === 'EMAIL_NOT_VERIFIED') {
          setUnverifiedEmail(error.email || userEmail);
        }
        console.log('There has been a problem with your login operation: ' + error.message);
      });
  };

  return (
    <Container fluid>
      <Row className="public-header-row">
        <div className="col public-header">
          <div className="d-flex justify-content-between">
            <a href="/">
              <img className="logo" src={bannerLogo} alt="Jookable logo image" />
            </a>
            <div />
          </div>
        </div>
      </Row>

      <Row className="g-0">
        <div className="col">
          <div className="public-page-content login-page-content">
            {secondStep ? (
              <TwoStepCodeStep
                mfaToken={secondStep.mfaToken}
                onSignedIn={(user, token) => signedIn(user, token, 'login', `${secondStep.method}_two_step`)}
                onCancel={() => setSecondStep(null)}
              />
            ) : googlePending ? (
              <GoogleUsernameStep
                pending={googlePending}
                onSignedIn={(user, token) => signedIn(user, token, 'sign_up')}
                onCancel={() => setGooglePending(null)}
              />
            ) : (
              <div className="public-page-formwrapper">
                <h1 className="public-page-formaccess">jookable&trade; Player </h1>
                <GoogleSignInButton mode="signin" onResult={handleGoogleResult} onError={showError} />
                <Form
                  onSubmit={e => {
                    e.preventDefault();
                    if (validateLoginForm(userEmail, userPassword, showError)) {
                      authHandler();
                    }
                  }}
                  className="public-page-form"
                >
                  {/* {this.state.error && <Alert color="danger">{this.state.error}</Alert>} */}
                  {error && <ErrorMessage errorMessage={error} />}
                  {unverifiedEmail && <ResendVerification email={unverifiedEmail} />}
                  <FormGroup>
                    <Input
                      type="email"
                      name="email"
                      placeholder="Email"
                      value={userEmail}
                      onChange={e => setUserEmail(e.target.value.trim())}
                    />
                  </FormGroup>
                  <FormGroup>
                    <Input
                      type="password"
                      name="password"
                      placeholder="Password"
                      value={userPassword}
                      onChange={e => setUserPassword(e.target.value.trim())}
                    />
                  </FormGroup>
                  <Button type="submit" disabled={loading} className="w-100">
                    {loading ? 'Loading...' : 'Log In'}
                  </Button>
                </Form>

                <div className="signin-link-wrapper">
                  <Link to="/signup" state={location.state} className="signin-link">
                    Need an Account?
                  </Link>
                  <br />
                  <br />
                  <Link to="/recover-password" className="signin-link">
                    Forgot Password?
                  </Link>
                  <br />
                  <br />
                  <Link to="/venues" className="signin-link">
                    Venues near you
                  </Link>
                </div>

                <div className="store-promo">
                  <h3 style={{ paddingTop: 15, textAlign: 'center' }}>
                    {' '}
                    {'Host your own jukebox on any TV, then add videos from any phone or computer.'}{' '}
                  </h3>
                </div>
              </div>
            )}
          </div>
        </div>
      </Row>

      <Row className="g-0">
        <div className="col">
          <AboutJookable />
        </div>
      </Row>

      <Row className="g-0">
        <div className="col footer">
          <FooterContent />
        </div>
      </Row>
    </Container>
  );
};

export default LoginView;
